import { User2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { OperatorId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { useCurrentOperator } from "@/hooks/use-operator";
import React, { useState } from "react";
import { skipToken } from "@tanstack/react-query";

interface BaseProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  tooltipContent?: React.ReactNode;
  showTooltip?: boolean;
}

export interface AvatarProps extends BaseProps {
  name?: string | null;
  imageUrl?: string | null;
}

export function Avatar({
  imageUrl,
  name,
  className,
  size = "md",
  tooltipContent,
  showTooltip = true,
}: AvatarProps) {
  const sizeClasses = {
    sm: "size-6 text-xs",
    md: "size-8 text-sm",
    lg: "size-12 text-base",
  };

  const getInitials = (name?: string | null): string => {
    if (!name) return "";
    return name
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  const initials = getInitials(name);
  // A profile photo that fails to load (expired, blocked) falls back to the
  // initials instead of a broken image. Keyed on the URL, so a new one retries.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = !!imageUrl && failedUrl !== imageUrl;

  const defaultTooltipContent = name ? (
    <div className="flex flex-col">
      <span>{name}</span>
    </div>
  ) : null;

  const avatarElement = (
    <div
      className={cn(
        "rounded-full bg-gray-100 flex shrink-0 items-center justify-center overflow-hidden select-none",
        sizeClasses[size],
        className,
      )}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={name || "Avatar"}
          className="size-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(imageUrl)}
        />
      ) : initials ? (
        <span className="font-medium text-gray-700">{initials}</span>
      ) : (
        <User2
          className={cn(
            size === "sm" ? "size-3" : size === "lg" ? "size-6" : "size-4",
          )}
        />
      )}
    </div>
  );

  if ((tooltipContent || defaultTooltipContent) && showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{avatarElement}</TooltipTrigger>
          <TooltipContent>
            {tooltipContent || defaultTooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return avatarElement;
}

export interface OperatorAvatarProps extends BaseProps {
  id: OperatorId | null;
}

export function OperatorAvatar({ id, ...props }: OperatorAvatarProps) {
  const { data: operator, isLoading } = trpc.operator.getOperatorById.useQuery(
    id ?? skipToken,
    {
      staleTime: 1000 * 60 * 10,
      gcTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  if (isLoading || !operator) return <OperatorAvatarSkeleton />;

  const displayName =
    operator.full_name ??
    `${operator.first_name ?? ""} ${operator.last_name ?? ""}`.trim() ??
    "Unknown Operator";

  return <Avatar imageUrl={operator.image_url} name={displayName} {...props} />;
}

export interface CurrentOperatorAvatarProps extends BaseProps {}

export function CurrentOperatorAvatar({
  ...props
}: CurrentOperatorAvatarProps) {
  const { data: operator } = useCurrentOperator();
  if (!operator) return <OperatorAvatarSkeleton />;
  return <OperatorAvatar id={operator.id} {...props} />;
}

export interface OperatorAvatarGroupProps extends BaseProps {
  operatorIds: OperatorId[];
}

export function OperatorAvatarGroup({
  operatorIds,
  size = "md",
  className,
  ...props
}: OperatorAvatarGroupProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const displayLimit = 3;
  const displayedOperatorIds =
    isHovered || operatorIds.length <= displayLimit
      ? operatorIds
      : operatorIds.slice(0, displayLimit);

  const remainingCount = operatorIds.length - displayLimit;

  const overlapClass = {
    sm: "-ml-2",
    md: "-ml-3",
    lg: "-ml-4",
  };

  const avatarSizeClass = {
    sm: "size-6",
    md: "size-8",
    lg: "size-12",
  };

  return (
    <div
      className={cn(
        "flex items-center transition-all duration-300 ease-in-out",
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {displayedOperatorIds.map((id, index) => (
        <div
          key={id}
          className={cn(
            "transition-all duration-300 ease-in-out",
            index > 0 && (isHovered ? "ml-1" : overlapClass[size]),
            "rounded-full border-2 border-background dark:border-gray-800",
          )}
          style={{ zIndex: operatorIds.length - index }}
        >
          <OperatorAvatar id={id} size={size} showTooltip={true} />
        </div>
      ))}
      {!isHovered &&
        operatorIds.length > displayLimit &&
        remainingCount > 0 && (
          <div
            className={cn(
              "flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium select-none",
              overlapClass[size],
              avatarSizeClass[size],
              "border-2 border-background dark:border-gray-800",
            )}
            style={{ zIndex: 0 }}
          >
            <span
              className={cn("text-xs", {
                "text-sm": size === "lg",
              })}
            >
              +{remainingCount}
            </span>
          </div>
        )}
    </div>
  );
}

function OperatorAvatarSkeleton({
  size = "md",
}: {
  size?: "sm" | "md" | "lg";
}) {
  const sizeClasses = {
    sm: "size-6",
    md: "size-8",
    lg: "size-12",
  };
  const iconSizeClasses = {
    sm: "size-3",
    md: "size-4",
    lg: "size-6",
  };
  return (
    <div
      className={cn(
        "rounded-full bg-gray-100 flex shrink-0 items-center justify-center overflow-hidden select-none animate-pulse",
        sizeClasses[size],
      )}
    >
      <User2 className={cn(iconSizeClasses[size], "opacity-50")} />
    </div>
  );
}
